import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, XCircle, Clock, MapPin, Navigation, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { API } from "@/lib/api";

const PHONE = "33783977370";

const PaymentResult = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const bookingId = params.get("booking");
  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("checking"); // checking | paid | pending | failed | notfound
  const [tries, setTries] = useState(0);
  const [cancelState, setCancelState] = useState(null); // null | sending | done | error
  const [cancelInfo, setCancelInfo] = useState(null);

  const requestCancel = async () => {
    setCancelState("sending");
    try {
      const r = await axios.post(`${API}/bookings/${bookingId}/cancel-request`, {});
      setCancelInfo(r.data);
      setCancelState("done");
    } catch {
      setCancelState("error");
    }
  };

  const poll = useCallback(async () => {
    if (!bookingId) { setState("notfound"); return; }
    try {
      const r = await axios.get(`${API}/bookings/${bookingId}/payment-status`);
      setBooking(r.data);
      const ps = r.data.payment_status;
      const wasPaid = ps === "paid" || ps === "refunded" || !!r.data.paid_at_paris_label;
      if (wasPaid) setState("paid");
      else if (ps === "failed") setState("failed");
      else setState("pending");
    } catch {
      setState("notfound");
    }
  }, [bookingId]);

  useEffect(() => { poll(); }, [poll]);

  // Keep polling a few times while SumUp finalizes (3DS etc.)
  useEffect(() => {
    if (state !== "pending" || tries >= 8) return;
    const id = setTimeout(() => { setTries((n) => n + 1); poll(); }, 2500);
    return () => clearTimeout(id);
  }, [state, tries, poll]);

  return (
    <>
      <SEO title="Réservation — VlaDriver VTC" noindex canonical="/payment/result" />
      <section className="min-h-[80vh] flex items-center justify-center py-28 px-4">
        <div data-testid="payment-result" className="w-full max-w-lg bg-[#0F1115] border border-white/10 rounded-3xl p-8 text-center">
          {state === "checking" && (
            <div className="py-8"><Loader2 size={32} className="animate-spin text-[#D4AF37] mx-auto mb-4" /><p className="text-white/60">{t("home.payChecking")}</p></div>
          )}

          {state === "paid" && booking && (
            <div data-testid="payment-success">
              <div className="w-16 h-16 rounded-full bg-[#25D366]/15 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={34} className="text-[#25D366]" />
              </div>
              <h1 className="font-serif text-3xl text-white mb-2">{t("home.paySuccessTitle")}</h1>
              <p className="text-white/55 text-sm mb-6">{t("home.paySuccessBody")}</p>

              {/* Legal VTC receipt — exact France local time of the order */}
              <div className="text-left bg-[#0A0B0D] border border-white/8 rounded-2xl p-5 space-y-2.5 text-sm">
                <div className="flex items-center gap-2 text-white/80"><MapPin size={14} className="text-[#D4AF37]" /> {booking.pickup}</div>
                <div className="flex items-center gap-2 text-white/80"><Navigation size={14} className="text-[#D4AF37]" /> {booking.destination}</div>
                <div className="flex items-center gap-2 text-white/80"><Clock size={14} className="text-[#D4AF37]" /> {booking.date} · {booking.time}</div>
                <div className="border-t border-white/5 pt-2.5 flex items-center justify-between">
                  <span className="text-white/50">{t("home.priceLabel")}</span>
                  <span className="font-serif text-2xl text-[#D4AF37]">{booking.price_label}</span>
                </div>
                <div className="text-[11px] text-white/40 pt-1" data-testid="payment-order-time">
                  {t("home.orderTime")} : <span className="text-white/70">{booking.created_at_paris_label}</span> {t("home.parisTime")}
                </div>
                {booking.paid_at_paris_label && (
                  <div className="text-[11px] text-white/40" data-testid="payment-paid-time">
                    {t("home.paidTime")} : <span className="text-white/70">{booking.paid_at_paris_label}</span> {t("home.parisTime")}
                  </div>
                )}
                <div className="text-[11px] text-white/35 pt-1">N° {booking.id}</div>
              </div>

              {/* Cancellation request — client asks, owner approves */}
              {booking.status === "cancelled" ? (
                <div data-testid="payment-cancelled" className="mt-5 text-sm text-red-300 bg-red-400/10 border border-red-400/20 rounded-2xl px-4 py-3">
                  Cette course a été annulée.{booking.refund_amount ? ` Remboursement : ${booking.refund_amount} €.` : ""}
                </div>
              ) : booking.cancel_requested || cancelState === "done" ? (
                <div data-testid="payment-cancel-done" className="mt-5 text-sm text-white/70 bg-white/5 border border-white/15 rounded-2xl px-4 py-3">
                  Demande d'annulation envoyée. Nous revenons vers vous rapidement pour confirmer et procéder au remboursement.
                  {cancelInfo?.refund_amount != null && (
                    <div className="text-[11px] text-white/45 mt-1">Remboursement estimé : {cancelInfo.refund_amount} € ({cancelInfo.refund_pct}%).</div>
                  )}
                </div>
              ) : (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={requestCancel}
                    disabled={cancelState === "sending"}
                    data-testid="payment-cancel-btn"
                    className="text-white/50 hover:text-red-300 text-xs underline underline-offset-4 transition-colors disabled:opacity-50"
                  >
                    {cancelState === "sending" ? "Envoi…" : "Demander l'annulation de la course"}
                  </button>
                  <p className="text-[11px] text-white/30 mt-1.5">Remboursement intégral si plus de 24h avant la course, 50% ensuite.</p>
                  {cancelState === "error" && <p className="text-[11px] text-red-400 mt-1">Échec de l'envoi. Appelez-nous ou réessayez.</p>}
                </div>
              )}

              <Link to="/" data-testid="payment-home-link" className="inline-flex items-center gap-2 mt-6 text-[#D4AF37] text-sm hover:underline">{t("home.backHome")} <ArrowRight size={14} /></Link>
            </div>
          )}

          {state === "pending" && (
            <div data-testid="payment-pending" className="py-6">
              <Loader2 size={30} className="animate-spin text-[#D4AF37] mx-auto mb-4" />
              <h1 className="font-serif text-2xl text-white mb-2">{t("home.payPendingTitle")}</h1>
              <p className="text-white/55 text-sm">{t("home.payPendingBody")}</p>
            </div>
          )}

          {(state === "failed" || state === "notfound") && (
            <div data-testid="payment-failed" className="py-6">
              <div className="w-16 h-16 rounded-full bg-red-400/15 flex items-center justify-center mx-auto mb-5"><XCircle size={32} className="text-red-400" /></div>
              <h1 className="font-serif text-2xl text-white mb-2">{t("home.payFailedTitle")}</h1>
              <p className="text-white/55 text-sm mb-6">{t("home.payFailedBody")}</p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/#booking" className="text-[#D4AF37] text-sm hover:underline">{t("home.payRetry")}</Link>
                <a href={`https://wa.me/${PHONE}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366] text-sm hover:underline">WhatsApp</a>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PaymentResult;
